import { useState } from 'react';
import { api, type DirectoryAgent } from '../lib/api';
import type { TicketPriority, TicketSource, TicketStatus } from '../lib/types';

/** Doc categories (provisioning-service's enum) — tracked tickets only. */
const ISSUE_CATEGORIES = [
  'GENERAL',
  'BILLING',
  'TECHNICAL',
  'ACTIVATION',
  'PORTING',
  'PLAN_CHANGE',
  'CANCELLATION',
  'FRAUD',
  'OTHER',
] as const;

/** The support-email endpoint has its own, lowercase, enum. Not the same list. */
const EMAIL_CATEGORIES = [
  'billing',
  'technical',
  'account',
  'coverage',
  'activation',
  'porting',
  'other',
] as const;

const PRIORITIES: TicketPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const STATUSES: TicketStatus[] = ['open', 'in_progress', 'waiting_customer', 'resolved'];

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_customer: 'Waiting on customer',
  resolved: 'Resolved',
  closed: 'Closed',
};

/** GENERAL → General, PLAN_CHANGE → Plan change. */
function pretty(v: string): string {
  const s = v.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function CreateTicketModal({
  tenantCode,
  agentUid,
  contactId,
  subscriberId,
  source = 'customer',
  chatId,
  agents,
  onClose,
}: {
  tenantCode: string;
  agentUid: string;
  /** Required for a customer ticket; an internal issue belongs to nobody. */
  contactId?: string;
  subscriberId?: string;
  /** 'internal' opens a tracked team issue instead of emailing support. */
  source?: TicketSource;
  chatId?: string;
  /** Directory for the "who owns it" picker (internal issues only). */
  agents?: DirectoryAgent[];
  onClose: (msg?: string) => void;
}) {
  const internal = source === 'internal';
  const [category, setCategory] = useState<string>(internal ? 'GENERAL' : 'technical');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('MEDIUM');
  const [area, setArea] = useState('');
  const [assigneeUid, setAssigneeUid] = useState('');
  const [status, setStatus] = useState<TicketStatus>('open');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      if (internal) {
        const res = await api.raiseTicket(tenantCode, {
          subject: subject.trim(),
          description: description.trim(),
          category,
          priority,
          source: 'internal',
          area: area.trim() || undefined,
          chatId,
        });
        // Owner and status aren't part of the create body — the POST only opens
        // the ticket. One follow-up PATCH carries over the spreadsheet's "who"
        // and "status" columns; a ticket that exists but missed them is still
        // better than no ticket, so the failure is reported, not thrown.
        let warn = '';
        if (assigneeUid || status !== 'open') {
          try {
            await api.updateTicket(tenantCode, res.ticketId, {
              assigneeUid: assigneeUid || undefined,
              status: status !== 'open' ? status : undefined,
            });
          } catch {
            warn = ' Owner/status were not applied — set them on the row.';
          }
        }
        onClose(`Issue ${res.ticketNumber || res.ticketId} created.${warn}`);
        return;
      }

      if (!contactId) {
        setErr('A support ticket needs a customer. Open it from the subscriber panel.');
        return;
      }
      const res = await api.createTicket(tenantCode, agentUid, contactId, {
        category,
        subject: subject.trim(),
        description: description.trim(),
        subscriberId,
        priority: priority.toLowerCase(),
      });
      if (res.ok) onClose(`Ticket created${res.ticketId ? `: ${res.ticketId}` : ''}.`);
      else setErr(res.error ?? 'Failed to create ticket');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => onClose()}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-semibold mb-3">
          {internal ? 'New team issue' : 'Create support ticket'}
        </div>
        <form onSubmit={submit} className="space-y-2.5">
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {(internal ? ISSUE_CATEGORIES : EMAIL_CATEGORIES).map((c) => (
                <option key={c} value={c}>{internal ? pretty(c) : c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">
              {internal ? 'What is it' : 'Subject'}
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">
              {internal ? 'Notes' : 'Description'}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              required
            />
          </div>
          {internal && (
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Area (optional)</label>
              <input
                type="text"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                maxLength={60}
                placeholder="app, billing run, porting…"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
          )}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-600 mb-0.5">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as TicketPriority)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{pretty(p)}</option>
                ))}
              </select>
            </div>
            {internal && (
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-0.5">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as TicketStatus)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {internal && (
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Owner</label>
              <select
                value={assigneeUid}
                onChange={(e) => setAssigneeUid(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="">Unassigned</option>
                {(agents ?? []).map((a) => (
                  <option key={a.uid} value={a.uid}>
                    {a.uid === agentUid ? `${a.displayName} (me)` : a.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}
          {err && <div className="text-xs text-red-600">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => onClose()}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
